import { useEffect, useState } from "react";
import api, { fmtMXN } from "@/lib/api";
import { Plus, Trash2, Package, Search } from "lucide-react";
import { toast } from "sonner";

const UNIDADES = [["E48", "E48 · Unidad de servicio"], ["ACT", "ACT · Actividad"], ["H87", "H87 · Pieza"], ["MTK", "MTK · Metro cuadrado"], ["LO", "LO · Lote"]];
const EMPTY = { description: "", product_key: "72111000", unit_key: "E48", price: "" };

export const SatProductsTab = ({ user }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sug, setSug] = useState([]);
  const canDelete = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/sat-products");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const searchKey = async () => {
    if (!form.description || form.description.length < 3) { toast.warning("Escribe la descripción primero"); return; }
    try {
      const { data } = await api.get("/sat-catalog/products", { params: { q: form.description } });
      const rows = data.results || data || [];
      setSug(rows);
      if (!rows.length) toast.info("Sin coincidencias en el catálogo SAT");
    } catch { toast.error("No se pudo buscar en el catálogo SAT"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/sat-products", { ...form, price: Number(form.price) || 0 });
      if (data.facturapi_error) toast.warning(`Guardado local, pero Facturapi respondió: ${data.facturapi_error}`);
      else toast.success("Producto registrado en Facturapi ✓");
      setOpen(false); setForm(EMPTY); setSug([]); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.description}"?`)) return;
    try { await api.delete(`/sat-products/${it.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-2" data-testid="sat-products-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-400">Catálogo de productos/servicios que facturas (se dan de alta en Facturapi y prellenan el timbrado).</div>
        <button data-testid="new-sat-product-btn" onClick={() => setOpen(true)} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo producto</button>
      </div>
      {items.length === 0 && (
        <div className="panel p-8 text-center text-slate-400 text-sm"><Package className="w-8 h-8 mx-auto mb-2 text-slate-600" />Sin productos. Registra tus servicios (ej. "Construcción de obra pública").</div>
      )}
      {items.map((it) => (
        <div key={it.id} className="panel px-4 py-3 flex items-center gap-3" data-testid={`sat-product-${it.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-100 truncate">{it.description}</div>
            <div className="text-[11px] font-mono text-slate-400">Clave {it.product_key} · Unidad {it.unit_key}{it.price > 0 && <> · {fmtMXN(it.price)}</>}</div>
          </div>
          {it.facturapi_product_id
            ? <span className="text-[10px] uppercase tracking-widest" style={{ color: "#86efac" }}>Facturapi ✓</span>
            : <span className="text-[10px] uppercase tracking-widest" title={it.facturapi_error || ""} style={{ color: "#f59e0b" }}>⚠ sin alta</span>}
          {canDelete && <button onClick={() => remove(it)} className="btn-ghost p-1.5" style={{ color: "#fca5a5" }} data-testid={`del-sat-product-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg panel-strong max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="sat-product-modal">
            <div className="label-mono">// NUEVO PRODUCTO / SERVICIO SAT</div>
            <div><label className="label-mono block mb-1">Descripción *</label>
              <textarea rows={2} required className="input-cyber px-3 py-2 text-sm w-full" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sat-product-desc" placeholder="Ej. CONSTRUCCIÓN DE OBRA PÚBLICA (ANTICIPOS Y ESTIMACIONES)" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="label-mono block mb-1">Clave producto/servicio SAT *</label>
                <div className="flex gap-1">
                  <input required className="input-cyber px-3 py-2 text-sm w-full font-mono" value={form.product_key} onChange={(e) => setForm({ ...form, product_key: e.target.value.replace(/\D/g, "") })} data-testid="sat-product-key" />
                  <button type="button" onClick={searchKey} title="Buscar clave en el catálogo SAT" className="btn-ghost px-2" data-testid="sat-key-search"><Search className="w-4 h-4" /></button>
                </div></div>
              <div><label className="label-mono block mb-1">Clave unidad</label>
                <select className="input-cyber px-2 py-2 text-sm w-full" value={form.unit_key} onChange={(e) => setForm({ ...form, unit_key: e.target.value })}>
                  {UNIDADES.map(([v, l]) => <option key={v} value={v} style={{ background: "#0a0f1d" }}>{l}</option>)}
                </select></div>
            </div>
            {sug.length > 0 && (
              <div className="panel p-2 space-y-1 max-h-40 overflow-y-auto">
                {sug.map((sx) => (
                  <button type="button" key={sx.key} onClick={() => { setForm({ ...form, product_key: sx.key }); setSug([]); }}
                    className="w-full text-left text-xs px-2 py-1 hover:bg-white/5 rounded-sm"><b className="font-mono text-cyan-300">{sx.key}</b> · {sx.description}</button>
                ))}
              </div>
            )}
            <div><label className="label-mono block mb-1">Precio de lista (opcional; sin precio se registra con $1 simbólico, el importe real va por factura)</label>
              <input type="number" step="0.01" min="0" className="input-cyber px-3 py-2 text-sm w-full font-mono text-right" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="sat-product-price" /></div>
            <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-sat-product-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">{saving ? "Registrando…" : "Registrar en Facturapi"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
